// SPDX-License-Identifier: GPL-3.0-or-later
// The HUD of the game page: score, ball, mode, timer, goal, ball saver, callout, run rows.
// Every text goes in with `textContent`. A view writes to a node only when the text changed.
// The ARIA live region gets at most one message each second (THEME.md section 11.4).

import type { GameStatus } from "../src/shared/contracts.ts";
import { formatPoints } from "../src/table/callouts.ts";
import { modeLabel } from "../src/table/modes.ts";
import { STAGE_COUNT, STAGES } from "../src/table/run/stages.ts";
import type { BossState, RunView } from "../src/table/run/types.ts";
import { type BarSlot, setBar, type TextSlot } from "./dom.ts";
import { eventLines, goalLine, stageLine } from "./run-text.ts";

/** The nodes of the HUD. */
export interface HudSlots {
  readonly score: TextSlot;
  readonly ball: TextSlot;
  readonly multiplier: TextSlot;
  readonly mode: TextSlot;
  readonly goal: TextSlot;
  readonly timerText: TextSlot;
  readonly timerBar: BarSlot;
  readonly saverText: TextSlot;
  readonly saverBar: BarSlot;
  /** The visible callout box. It is hidden from screen readers; the live region speaks. */
  readonly callout: TextSlot;
}

/** A visible callout stays this long. */
export const CALLOUT_VISIBLE_MS = 3000;
/** The live region gets one message in this time at most. */
export const LIVE_MIN_GAP_MS = 1000;
/** The ball saver bar uses this as the full length when no longer time was seen. */
const DEFAULT_SAVER_MS = 10_000;

/** The HUD API that the event router and the loop use. */
export interface Hud {
  /** Show the game status. Call it at each frame. */
  update(status: GameStatus): void;
  /** Show a callout for CALLOUT_VISIBLE_MS. */
  showCallout(text: string, nowMs: number): void;
  setTimer(secondsLeft: number, secondsTotal: number): void;
  clearTimer(): void;
  setGoal(text: string): void;
  /** Hide the callout when its time is over. Call it at each frame. */
  tick(nowMs: number): void;
}

function put(node: TextSlot, text: string): void {
  if (node.textContent !== text) node.textContent = text;
}

/** Text of the ball field. */
export function ballText(status: Pick<GameStatus, "ballNumber" | "ballsPerGame">): string {
  return `BALL ${status.ballNumber > 0 ? status.ballNumber : "-"} / ${status.ballsPerGame}`;
}

/** Text of the mode field: the labels of the active modes, or a fixed text. */
export function modeText(activeModes: readonly string[]): string {
  return activeModes.length > 0
    ? activeModes.map((id) => modeLabel(id)).join(" + ")
    : "NO MODE ACTIVE";
}

/** Make the HUD. */
export function createHud(slots: HudSlots): Hud {
  let calloutUntil = 0;
  let saverFull = DEFAULT_SAVER_MS;

  slots.callout.hidden = true;
  setBar(slots.timerBar, 0);
  setBar(slots.saverBar, 0);

  return {
    update(status) {
      put(slots.score, formatPoints(status.score));
      put(slots.ball, ballText(status));
      put(slots.multiplier, `BONUS ${status.multiplier}X`);
      put(slots.mode, modeText(status.activeModes));
      const left = status.ballSaveMsLeft;
      if (left > 0) {
        saverFull = Math.max(saverFull, left);
        put(slots.saverText, `SAVER ${Math.ceil(left / 1000)} s`);
        setBar(slots.saverBar, left / saverFull);
      } else {
        saverFull = DEFAULT_SAVER_MS;
        put(slots.saverText, "SAVER OFF");
        setBar(slots.saverBar, 0);
      }
    },
    showCallout(text, nowMs) {
      slots.callout.textContent = text;
      slots.callout.hidden = false;
      calloutUntil = nowMs + CALLOUT_VISIBLE_MS;
    },
    setTimer(secondsLeft, secondsTotal) {
      put(slots.timerText, `${secondsLeft} s`);
      setBar(slots.timerBar, secondsTotal > 0 ? secondsLeft / secondsTotal : 0);
    },
    clearTimer() {
      put(slots.timerText, "");
      setBar(slots.timerBar, 0);
    },
    setGoal(text) {
      put(slots.goal, text);
    },
    tick(nowMs) {
      if (!slots.callout.hidden && nowMs >= calloutUntil) slots.callout.hidden = true;
    },
  };
}

/** A message for the live region. `high` messages go before `low` ones. */
export type LivePriority = "high" | "low";

/** The live region feed. It keeps the one-message-a-second limit. */
export interface LiveFeed {
  push(text: string, priority: LivePriority): void;
  /** Write the next message when the gap is over. Call it at each frame. */
  tick(nowMs: number): void;
}

/**
 * Make the feed. High messages wait in order (at most 8). A low message replaces the last
 * waiting low message, so a burst of score events does not push out a callout.
 */
export function createLiveFeed(region: TextSlot): LiveFeed {
  const high: string[] = [];
  let low: string | null = null;
  let lastWrite = Number.NEGATIVE_INFINITY;
  return {
    push(text, priority) {
      if (priority === "high") {
        if (high.length >= 8) high.shift();
        high.push(text);
      } else {
        low = text;
      }
    },
    tick(nowMs) {
      if (nowMs - lastWrite < LIVE_MIN_GAP_MS) return;
      let next = high.shift();
      if (next === undefined && low !== null) {
        next = low;
        low = null;
      }
      if (next === undefined) return;
      // The same text twice is not announced again: clear the node first.
      region.textContent = "";
      region.textContent = next;
      lastWrite = nowMs;
    },
  };
}

/** The run rows of the HUD (design section 9.2). */
export interface RunHudSlots {
  readonly stage: TextSlot;
  readonly stageGoal: TextSlot;
  readonly event1: TextSlot;
  readonly event2: TextSlot;
  readonly event3: TextSlot;
}

/** The run HUD API. The loop calls it at each frame. */
export interface RunHud {
  /** Show the stage, the goal and the three event rows of the run. */
  updateRun(view: RunView): void;
}

/**
 * A writer for one node. It keeps the last text it wrote, so a frame with the same text
 * writes nothing. It never reads `textContent` back.
 */
function cachedWriter(node: TextSlot): (text: string) => void {
  let last: string | null = null;
  return (text) => {
    if (text === last) return;
    last = text;
    node.textContent = text;
  };
}

/** Make the run HUD. Each row keeps its last text. */
export function createRunHud(slots: RunHudSlots): RunHud {
  const writeStage = cachedWriter(slots.stage);
  const writeGoal = cachedWriter(slots.stageGoal);
  const writeEvent1 = cachedWriter(slots.event1);
  const writeEvent2 = cachedWriter(slots.event2);
  const writeEvent3 = cachedWriter(slots.event3);

  return {
    updateRun(view) {
      const def = STAGES[view.stageIndex];
      if (def === undefined) {
        writeStage("");
        writeGoal("");
        writeEvent1("");
        writeEvent2("");
        writeEvent3("");
        return;
      }
      writeStage(stageLine(def, STAGE_COUNT));
      writeGoal(goalLine(def, view.signals, view.boss));
      const [row1, row2, row3] = eventLines(view.events, view.extraBall, def.hurryShot);
      writeEvent1(row1);
      writeEvent2(row2);
      writeEvent3(row3);
    },
  };
}

/** The boss bar of the HUD. It sits on the playfield and shows only while a boss fights. */
export interface BossHudSlots {
  /** The box of the bar. It is hidden while no boss is active. */
  readonly root: TextSlot;
  readonly name: TextSlot;
  readonly hp: TextSlot;
  /** The health bar. It has the role "progressbar". */
  readonly bar: BarSlot;
  /** The shield or wind-up state as text. Empty when there is none. */
  readonly state: TextSlot;
}

/** The boss HUD API. The loop calls it at each frame. */
export interface BossHud {
  /** Show the boss, or hide the bar when no boss is active. */
  update(boss: Readonly<BossState>): void;
}

/** The text of the HP field: the HP now over the most HP. A bad number gives 0. */
export function bossHpText(boss: Pick<BossState, "hp" | "maxHp">): string {
  const whole = (v: number): number => (Number.isFinite(v) ? Math.max(0, Math.ceil(v)) : 0);
  return `${whole(boss.hp)}/${whole(boss.maxHp)}`;
}

/** The label of the bar for a screen reader, with the HP numbers and the shield state. */
export function bossBarLabel(boss: Readonly<BossState>): string {
  const shield = boss.shielded ? ", shield up" : "";
  return `Boss health ${bossHpText(boss).replace("/", " of ")}${shield}`;
}

/** The state text of the boss bar. The shield wins over the wind-up. */
export function bossStateText(boss: Pick<BossState, "shielded" | "telegraph">): string {
  if (boss.shielded) return "SHIELD";
  return boss.telegraph > 0 ? "WIND-UP" : "";
}

/** A writer for one attribute. It keeps the last value, so a frame with the same value writes nothing. */
function cachedAttr(node: TextSlot, attr: string): (value: string) => void {
  let last: string | null = null;
  return (value) => {
    if (value === last) return;
    last = value;
    node.setAttribute(attr, value);
  };
}

/** Make the boss HUD. Every node keeps its last value, so a steady frame writes nothing. */
export function createBossHud(slots: BossHudSlots): BossHud {
  const writeName = cachedWriter(slots.name);
  const writeHp = cachedWriter(slots.hp);
  const writeState = cachedWriter(slots.state);
  const writeLabel = cachedAttr(slots.bar, "aria-label");
  const writeShield = cachedAttr(slots.bar, "data-shield");
  let lastFraction = Number.NaN;
  slots.root.hidden = true;

  return {
    update(boss) {
      if (!boss.active) {
        if (!slots.root.hidden) slots.root.hidden = true;
        return;
      }
      if (slots.root.hidden) slots.root.hidden = false;
      writeName(`BOSS ${boss.name.toUpperCase()}`);
      writeHp(bossHpText(boss));
      writeState(bossStateText(boss));
      writeLabel(bossBarLabel(boss));
      writeShield(boss.shielded ? "on" : "off");
      const fraction = boss.maxHp > 0 ? boss.hp / boss.maxHp : 0;
      if (fraction !== lastFraction) {
        lastFraction = fraction;
        setBar(slots.bar, fraction);
      }
    },
  };
}
